import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import type { CustomerActivityRow } from "@/hooks/use-customer-activities";
import { activityTypeLabel, stripQuotedReply } from "@/lib/customer-activity-calc";
import { formatRelativeTime } from "@/lib/format";
import { formatSendTime } from "@/lib/send-window";
import { TONE_TEXT } from "@/lib/widget-status";
import type { CustomerActivityType } from "@/lib/db-types";

const ICONS: Record<CustomerActivityType, string> = {
  email_out: "send",
  email_in: "mail",
  call: "phone",
  sms_out: "message-square",
  meeting: "calendar",
  note: "sticky-note",
  summary: "scroll-text",
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("nb-NO", { timeZone: "Europe/Oslo", hour: "2-digit", minute: "2-digit" });
}

interface ActivityItemProps {
  row: CustomerActivityRow;
  /** «Siste kontakt» i sidekolonnen: relativ tid i stedet for klokkeslett, og ingen «Vis hele». */
  compact?: boolean;
}

/** Én rad i kontaktloggen: ikon per type, emne, hvem og når, og de første linjene av innholdet. */
export default function ActivityItem({ row, compact = false }: ActivityItemProps) {
  const [expanded, setExpanded] = useState(false);
  const scheduled = row.status === "scheduled" && row.send_at;
  const failed = row.status === "failed";
  const who = row.type === "email_in" ? row.contact?.full_name : row.user?.full_name;
  const withContact = row.contact?.full_name && row.type !== "email_in" ? ` til ${row.contact.full_name}` : "";
  // Innkommende e-post: skjul sitert tekst i visningen (full tekst er lagret).
  const shown = row.type === "email_in" && row.body ? stripQuotedReply(row.body) : row.body ?? "";
  const longBody = shown.split("\n").length > 2 || shown.length > 160;

  return (
    <li className="flex gap-3 py-3 border-b border-border/50 last:border-0">
      <SrIcon
        name={scheduled ? "clock" : ICONS[row.type]}
        className="w-4 h-4 mt-1 shrink-0 text-muted-foreground"
        aria-label={activityTypeLabel(row.type)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-medium">{row.subject || activityTypeLabel(row.type)}</span>
          <span className="text-xs text-muted-foreground">
            {activityTypeLabel(row.type)}
            {withContact}
            {who ? ` · ${who}` : ""} · {compact ? formatRelativeTime(row.occurred_at) : formatTime(row.occurred_at)}
          </span>
        </div>
        {scheduled && (
          <p className={`text-xs ${TONE_TEXT.near}`}>Sendes {formatSendTime(new Date(row.send_at!))}</p>
        )}
        {failed && (
          <p className={`text-xs ${TONE_TEXT.miss}`}>Sending feilet{row.error ? `: ${row.error}` : ""}</p>
        )}
        {row.body && (
          <p className={`text-sm text-muted-foreground whitespace-pre-wrap ${expanded ? "" : "line-clamp-2"}`}>{shown}</p>
        )}
        {row.body && longBody && !compact && (
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Vis mindre" : "Vis hele"}
          </Button>
        )}
      </div>
    </li>
  );
}
